import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { map } from 'rxjs';

import { MESSAGE_LIMIT, THREAD_LIMIT } from '../models/chat.const';
import { ChatMessage, ChatMessageRequest, ChatThread, ChatThreadSearchRequest, PagedResponse } from '../models/chat.interface';
import { CHAT_API_URL } from '../tokens/chat.tokens';

@Injectable({
  providedIn: 'root',
})
export class ChatCommunicationService {
  private readonly http = inject(HttpClient);
  private readonly chatApiUrl = inject(CHAT_API_URL);

  getThread(threadId: string) {
    return this.http.get<ChatThread>(`${this.chatApiUrl}/communications/threads/${threadId}`).pipe(map(response => this.mapChatThread(response)));
  }

  searchThreads(query: ChatThreadSearchRequest) {
    const params = new URLSearchParams();

    if (query.value != null) params.set('value', query.value);
    if (query.page != null) params.set('page', query.page.toString());
    params.set('pageSize', query.pageSize.toString() ?? THREAD_LIMIT.toString());
    params.set('sortBy', query.sortBy ?? 'last_sent_at');
    params.set('sortDirection', query.sortDirection ?? 'desc');

    return this.http.get<PagedResponse<ChatThread>>(`${this.chatApiUrl}/communications/threads/search?${params.toString()}`).pipe(
      map(response => {
        response.value = response.value.map(thread => this.mapChatThread(thread));
        return response;
      })
    );
  }

  searchChatMessages(threadId: string, query?: ChatMessageRequest) {
    return this.http
      .get<PagedResponse<ChatMessage>>(`${this.chatApiUrl}/communications/messages/search`, {
        params: Object.entries({
          threadId,
          page: query.page,
          pageSize: query.pageSize ?? MESSAGE_LIMIT,
          from: query.from?.toISOString(),
          to: query.to?.toISOString(),
          sortBy: 'created_at',
          sortDirection: 'desc',
        }).reduce((acc, [key, value]) => {
          if (value) {
            acc[key] = value;
          }
          return acc;
        }, {}),
      })
      .pipe(
        map(response => {
          response.value = response.value.map(message => this.mapChatMessage(message));
          return response;
        })
      );
  }

  //TODO: DO NOT USE AT ANY CIRCUMSTANCES - should be removed ASAP
  getUnreadCounts() {
    return this.http.get<{ [key: string]: string }>(`${this.chatApiUrl}/communications/threads/unread/count?readByLiloTeam=true`);
  }

  getUnreadCountsByThreadIds(threads: ChatThread[]) {
    return this.http.post<{ [key: string]: string }>(`${this.chatApiUrl}/communications/threads/unread/count?readByLiloTeam=true`, threads.map(t => t.id));
  }

  getUnreadCount(threadId: string) {
    return this.http.get<{
      count: number;
    }>(`${this.chatApiUrl}/communications/threads/${threadId}/unread/count`);
  }

  markThreadRead(threadId: string) {
    return this.http.put<void>(`${this.chatApiUrl}/communications/threads/${threadId}/read`, {});
  }

  getMessage(messageId: string) {
    return this.http.get<ChatMessage>(`${this.chatApiUrl}/communications/messages/${messageId}`).pipe(
      map(response => {
        return this.mapChatMessage(response);
      })
    );
  }

  sendMessage(message: Partial<ChatMessage>) {
    return this.http.post<ChatMessage>(`${this.chatApiUrl}/communications/messages`, message).pipe(
      map(response => {
        return this.mapChatMessage(response);
      })
    );
  }

  getFileContents(link: string) {
    return this.http.get(`${this.chatApiUrl}/communications/files/link?link=${encodeURI(link)}`, { responseType: 'blob' });
  }

  uploadFiles(payload: any) {
    return this.http.post(`${this.chatApiUrl}/communications/files/upload`, payload);
  }

  assignThread(threadId: string) {
    return this.http.put<void>(`${this.chatApiUrl}/communications/threads/${threadId}/assign`, {});
  }

  unassignThread(threadId: string) {
    return this.http.put<void>(`${this.chatApiUrl}/communications/threads/${threadId}/unassign`, {});
  }

  private mapChatThread(thread: ChatThread) {
    return {
      ...thread,
      lastSentAt: thread.lastSentAt ? new Date(thread.lastSentAt) : null,
      deletedAt: thread.deletedAt ? new Date(thread.deletedAt) : null,
      updatedAt: new Date(thread.updatedAt),
      createdAt: new Date(thread.createdAt),
    };
  }

  private mapChatMessage(message: ChatMessage) {
    return {
      ...message,
      updatedAt: new Date(message.updatedAt),
      deletedAt: message.deletedAt ? new Date(message.deletedAt) : null,
      createdAt: new Date(message.createdAt),
    };
  }
}
